import messageIdMapping from "../../IdMappings/MessageIdMapping.ts";
import {MessageNode} from "../../messageObject/MessageNode.ts";
import {getIdToken} from "../../misc/miscUtils.ts";
import {URL_BASE} from "../../../Globals.ts";

const TURNS_TO_KEEP = 5;

/**
 * Walk the message tree following the current sequence and return a flat array of all message nodes.
 */
const getAllMessages = (rootNode: any, messagesSequence: number[]): MessageNode[] => {
    const allMessages: MessageNode[] = [];
    let currentNode: MessageNode | null = rootNode;

    for (const sequenceIndex of messagesSequence) {
        if (currentNode?.children && sequenceIndex < currentNode.children.length) {
            currentNode = messageIdMapping[currentNode.children[sequenceIndex]];
        } else {
            break;
        }
        if (currentNode?.message && !currentNode.message.isLoading) {
            allMessages.push(currentNode);
        }
    }

    return allMessages;
};

/**
 * Count the total number of completed user turns (non-temp user messages).
 */
const getTotalTurnCount = (allMessages: MessageNode[]): number => {
    let count = 0;
    for (const node of allMessages) {
        if (node.message.role === 'user' && !node.message.isTemp) {
            count++;
        }
    }
    return count;
};

/**
 * Get all messages belonging to a specific turn number (1-indexed).
 * A turn starts with a user message and includes all subsequent assistant/tool messages
 * until the next user message.
 */
const getMessagesForTurn = (allMessages: MessageNode[], turnNumber: number): MessageNode[] => {
    let userCount = 0;
    let startIndex = -1;

    for (let i = 0; i < allMessages.length; i++) {
        if (allMessages[i].message.role === 'user' && !allMessages[i].message.isTemp) {
            userCount++;
            if (userCount === turnNumber) {
                startIndex = i;
                break;
            }
        }
    }

    if (startIndex === -1) return [];

    const turnMessages: MessageNode[] = [allMessages[startIndex]];
    for (let i = startIndex + 1; i < allMessages.length; i++) {
        if (allMessages[i].message.role === 'user' && !allMessages[i].message.isTemp) break;
        turnMessages.push(allMessages[i]);
    }

    return turnMessages;
};

/**
 * Find the most recent summary from assistant messages in the conversation,
 * excluding the current AI message (which hasn't had its summary set yet).
 */
const findPreviousSummary = (allMessages: MessageNode[], currentAiMessageId: string | null): string | null => {
    for (let i = allMessages.length - 1; i >= 0; i--) {
        const node = allMessages[i];
        if (node.id === currentAiMessageId) continue;
        if (node.message.role === 'assistant' && node.message.summary) {
            return node.message.summary;
        }
    }
    return null;
};

/**
 * Extract simplified text content from message nodes for summary generation.
 */
const extractTextFromMessages = (messages: MessageNode[]): Array<{ role: string; content: string }> => {
    return messages.map(node => {
        const msg = node.message;
        let textContent = '';

        if (Array.isArray(msg.content)) {
            textContent = msg.content
                .map((item: any) => {
                    if (item.type === 'text') return item.text;
                    if (item.type === 'file') return `[File: ${item.fileName}]\n${item.fileContent || ''}`;
                    if (item.type === 'link') return `[Link: ${item.fileName}]\n${item.fileContent || ''}`;
                    if (item.type === 'image') return '[Image attached]';
                    if (item.type === 'audio') return item.transcript ? `[Audio transcript]: ${item.transcript}` : '[Audio attached]';
                    if (item.type === 'video') return '[Video attached]';
                    return '';
                })
                .filter(Boolean)
                .join('\n');
        } else if (typeof msg.content === 'string') {
            textContent = msg.content;
        }

        return { role: msg.role, content: textContent };
    });
};

/**
 * Call the server endpoint to generate an updated summary.
 */
const callGenerateSummaryEndpoint = async (
    user: any,
    currentSummary: string | null,
    turnMessages: Array<{ role: string; content: string }>
): Promise<string | null> => {
    try {
        const token = await getIdToken(user);
        const response = await fetch(`${URL_BASE}/api/generateSummary`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ currentSummary, turnMessages })
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        return data.summary ?? null;
    } catch (error) {
        console.error("Failed to generate summary:", error);
        return currentSummary; // Fall back to previous summary so we don't lose context
    }
};

/**
 * Generate a summary of the newly truncated turn and attach it to the current AI message.
 * Called right when the AI finishes its response stream.
 *
 * At the end of turn N (N >= TURNS_TO_KEEP):
 *   - Turn (N - TURNS_TO_KEEP + 1) is the newly truncated turn
 *   - Previous summary (from the prior assistant message) covers turns 1..(N - TURNS_TO_KEEP)
 *   - We send the previous summary + the newly truncated turn's messages to the server
 *   - The server returns an updated summary covering turns 1..(N - TURNS_TO_KEEP + 1)
 *   - We attach it to the current AI message for use in the next turn
 */
export const generateAndAttachSummary = async (
    messagesRef: any,
    messagesSequenceRef: any,
    llmRequestRefs: any,
    userObjRef: any
): Promise<void> => {
    try {
        const user = userObjRef.current.user;
        if (!user) return;

        const allMessages = getAllMessages(messagesRef.current, messagesSequenceRef.current);
        const totalTurns = getTotalTurnCount(allMessages);

        if (totalTurns < TURNS_TO_KEEP) return;

        const turnToSummarize = totalTurns - TURNS_TO_KEEP + 1;
        const currentAiMessageId = llmRequestRefs.current.aiMessage?.id ?? null;
        const previousSummary = turnToSummarize > 1
            ? findPreviousSummary(allMessages, currentAiMessageId)
            : null;

        const turnMessages = getMessagesForTurn(allMessages, turnToSummarize);
        if (turnMessages.length === 0) return;

        const simplifiedMessages = extractTextFromMessages(turnMessages);
        const newSummary = await callGenerateSummaryEndpoint(user, previousSummary, simplifiedMessages);

        if (newSummary && llmRequestRefs.current.aiMessage?.message) {
            llmRequestRefs.current.aiMessage.message.summary = newSummary;
        }
    } catch (error) {
        console.error("Error in generateAndAttachSummary:", error);
    }
};

/**
 * Search trimmed messages backwards for the most recent assistant message with a summary.
 * Used when formatting messages for an LLM request to inject context about truncated turns.
 */
export const findSummaryForInjection = (trimmedMessages: any[]): string | null => {
    for (let i = trimmedMessages.length - 1; i >= 0; i--) {
        const msg = trimmedMessages[i]?.message;
        if (msg?.role === 'assistant' && msg.summary) {
            return msg.summary;
        }
    }
    return null;
};

/**
 * Format a summary string for injection into a prompt prefix.
 */
export const formatSummaryForPrompt = (summary: string): string => {
    return `<summary_of_earlier_conversation>\n${summary}\n</summary_of_earlier_conversation>\n\n`;
};
